import { cn } from "@/libs/utils";
import { useTrans } from "@/utils/useTrans";
import Image from "next/image";
import { highlightHtml } from "@/components/tactsuit/tactglove-dk3/highlightHtml";

type CardData = {
  imageSrc: string;
  imageAlt: string;
  title: string;
  description?: string;
  asterisk?: string;
};

/** 데스크톱: 썸네일(348h) 위 + 텍스트 아래 stack. 모바일은 별도 MobileCard. */
const Card = ({ data }: { data: CardData }) => (
  <div className="flex w-full flex-col items-start gap-5">
    <div className="relative h-[348px] w-full overflow-hidden rounded-2xl border border-coolgray-20 laptop:border-none">
      <Image
        src={data.imageSrc}
        alt={data.imageAlt}
        fill
        sizes="(max-width: 1024px) 85vw, 33vw"
        className="object-cover"
      />
    </div>
    <div className="flex w-full flex-col gap-3">
      <h3 className="w-full text-[22px] font-400 leading-7 text-coolgray-90">
        {data.title}
      </h3>
      {data.description && (
        <p className="text-sm font-400 leading-[18px] tracking-[0.25px] text-coolgray-60">
          {data.description}
        </p>
      )}
      {data.asterisk && (
        <p className="text-sm font-400 leading-[18px] tracking-[0.25px] text-coolgray-90">
          {data.asterisk}
        </p>
      )}
    </div>
  </div>
);

/** 모바일: 280×210 (4:3) 썸네일 + 하단 text stack. 캐러셀 슬라이드용. */
const MobileCard = ({ data }: { data: CardData }) => (
  <div className="flex h-full w-full flex-col gap-6">
    <div className="relative aspect-[280/210] w-full overflow-hidden rounded-2xl border border-coolgray-20">
      <Image
        src={data.imageSrc}
        alt={data.imageAlt}
        fill
        sizes="(max-width: 1024px) 85vw, 33vw"
        className="object-cover"
      />
    </div>
    <div className="flex flex-col gap-3 px-1">
      <h3 className="text-2xl font-400 leading-8 text-coolgray-90">
        {data.title}
      </h3>
      {data.description && (
        <p className="text-sm font-400 leading-[18px] tracking-[0.25px] text-coolgray-60">
          {data.description}
        </p>
      )}
      {data.asterisk && (
        <p className="text-sm font-400 leading-[18px] tracking-[0.25px] text-coolgray-90">
          {data.asterisk}
        </p>
      )}
    </div>
  </div>
);

export default function Dk3Performance() {
  const { t } = useTrans(["tactsuit"]);

  // Figma 순서. Row1: headsets / bluetooth / anti-slip. Row2: magnetic / washable / comfort.
  const cards: CardData[] = [
    {
      imageSrc: "/images/tactsuit/tactglove-dk3/perf-headsets.jpg",
      imageAlt: "Hand tracking with VR headset",
      title: t("Optimized Hand Tracking Compatibility"),
      description: t(
        "Without internal tracking sensors, TactGlove complements your headset's native hand-tracking, focusing entirely on delivering high-fidelity feedback.",
      ),
      asterisk: t(
        "*Tested with Meta Quest 3, the DK3 delivers ~90% of bare‑hand tracking accuracy.",
      ),
    },
    {
      imageSrc: "/images/tactsuit/tactglove-dk3/perf-bluetooth.jpg",
      imageAlt: "Bluetooth 5 wireless",
      title: t("Bluetooth 5.0 Low Latency."),
      description: t(
        "Pure wireless freedom. No cables. No breaks in immersion.",
      ),
    },
    {
      imageSrc: "/images/tactsuit/tactglove-dk3/perf-anti-slip.jpg",
      imageAlt: "Touchscreen compatible fingertips",
      title: t("Anti-slip fingertips. Touchscreen compatible."),
      description: t(
        "Touchscreen compatible and textured for a precise, secure grip.",
      ),
    },
    {
      imageSrc: "/images/tactsuit/tactglove-dk3/perf-magnetic.jpg",
      imageAlt: "Magnetic strap",
      title: t("Magnetic strap. On in one motion."),
      description: t("Self-aligning · no buckles"),
    },
    {
      imageSrc: "/images/tactsuit/tactglove-dk3/perf-washable.jpg",
      imageAlt: "Washable inner glove",
      title: t("Washable inner glove."),
      description: t(
        "The antimicrobial, machine-washable inner glove is easily removable for effortless personal hygiene.",
      ),
      asterisk: t(
        "*For high-traffic shared environments, we highly recommend using disposable inner gloves to ensure optimal hygiene for every user.",
      ),
    },
    {
      imageSrc: "/images/tactsuit/tactglove-dk3/perf-comfort.jpg",
      imageAlt: "Breathable fabric",
      title: t("Light. Breathable. All-session comfort."),
      description: t(
        "Soft elastic material. No pressure points across long sessions.",
      ),
    },
  ];

  return (
    <section className="w-full bg-white">
      <div className="mx-auto flex w-full max-w-screen-max flex-col gap-8 laptop:gap-10">
        {/* Header */}
        <header className="mx-auto flex max-w-[708px] flex-col items-center gap-4 px-4 text-center laptop:px-8">
          <h2 className="text-2xl font-400 leading-8 text-coolgray-90 laptop:text-[32px] laptop:leading-10">
            {t("Engineered for Performance")}
          </h2>
          <p
            className="hidden text-sm font-400 leading-[18px] tracking-[0.25px] text-coolgray-60 laptop:block laptop:text-base laptop:leading-[22px] laptop:tracking-[0.5px]"
            dangerouslySetInnerHTML={{
              __html: highlightHtml(
                t(
                  "Bluetooth 5.0 with a comfortable material and a one-motion magnetic strap. TactGlove DK3 is compatible with camera based hand tracking.",
                ),
              ),
            }}
          />
        </header>

        {/* 모바일: native scroll-snap horizontal carousel.
            scroll-pl-4 로 각 카드 snap 위치가 viewport 좌측 16px 에 고정됨.
            카드 폭은 viewport 85% (반응형). */}
        <div className="snap-x snap-mandatory scroll-pl-4 overflow-x-auto pl-4 pr-4 [scrollbar-width:none] laptop:hidden [&::-webkit-scrollbar]:hidden">
          <div className="flex w-max gap-4">
            {cards.map((card) => (
              <div key={card.imageSrc} className="w-[85vw] shrink-0 snap-start">
                <MobileCard data={card} />
              </div>
            ))}
          </div>
        </div>

        {/* 데스크톱: 2 rows × 3 cards (1fr each) */}
        <div
          className={cn(
            "hidden px-8 laptop:grid laptop:grid-cols-3 laptop:gap-6",
          )}
        >
          {cards.map((card) => (
            <Card key={card.imageSrc} data={card} />
          ))}
        </div>
      </div>
    </section>
  );
}
